import { AppType } from "client/Api/Apps/common";
import {
	FormItem,
	InputLike,
	TextInput,
	ValidationError }
	 from "client/Components";
import { AppDestinationWindowSelectFormItem } from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import { IFormHandler, useFormHandler } from "client/Forms";
import { createValidationConfiguration } from "client/Validation";
import {
	doesMatchRegExp,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useImperativeHandle,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	conditionalAccessValidator,
	initialConditionalAccessState,
	prepareConditionalAccessForSaving
} from "../ConditionalAccess";
import { EditAppHandle } from "../EditApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import {
	initialMaintenanceWindowState,
	maintenanceWindowValidator,
	prepareMaintenanceWindowForSaving
} from "../MaintenanceWindow";
import { useSaveApp, EditAppSavedResult } from "../useSaveApp";
import styles from "./EditAzureApp.module.less";
import {
	SaveAzureAppAppModel,
	EditAzureAppViewModel,
	saveAzureApp
} from "client/Api/Apps/EditApp/saveApp";

const placeholder = "application/43289432342";

const validation = createValidationConfiguration<SaveAzureAppAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validation.forScalar("urlSuffix")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesMatchRegExp(
			/^[^\s\/\\]+\/[^\s\/\\]+$/,
			localize("_DoesNotMatchAzureAppUrlSuffixPattern")
				.replace("{urlSuffixExample}", placeholder)
		)
	);

validation.forObject("conditionalAccessSettings")
	.addValidators(conditionalAccessValidator());

validation.forObject("maintenanceSettings")
	.addValidators(maintenanceWindowValidator());


export type EditAzureAppProps = {
	initialSettings: EditAppSettings<EditAzureAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditAzureApp = forwardRef(
	(
		props: EditAzureAppProps,
		ref: Ref<EditAppHandle>
	): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const [formData, setFormData] = useState<SaveAzureAppAppModel>(
			() => convertToSaveModel(initialSettings)
		);

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const { canBeConfigured } = app;

		const formHandler: IFormHandler<SaveAzureAppAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const prepareModelForSaving = useCallback(
			(model: SaveAzureAppAppModel): SaveAzureAppAppModel => {
				return {
					...model,
					urlSuffix: canBeConfigured
						? model.urlSuffix
						: null,
					maintenanceSettings: prepareMaintenanceWindowForSaving(model.maintenanceSettings),
					conditionalAccessSettings: prepareConditionalAccessForSaving(model.conditionalAccessSettings)
				};
			},
			[canBeConfigured]
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveAzureAppAppModel>(
			formHandler,
			saveAzureApp,
			AppType.AzureApp,
			prepareModelForSaving
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under("urlSuffix", "destinationWindow")
			.attachToTab()
			.hasValidationError;

		return (
			<EditAppBase
				formHandler={formHandler}
				hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
				initialAppSettings={app}
				usersAndUserGroupsData={usersAndUserGroupsData}
				initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
			>
				<>
					{canBeConfigured && (
						<FormItem label={localize("_SSOUrl")}>
							<div className={styles.ssoUrlContainer}>
								<div className={styles.ssoUrlPrefix}>
									<InputLike
										value={app.urlPrefix}
										trackingId="sso-url"
										attachedOn="right"
										isInvalid={
											formHandler.for("urlSuffix")
												.attachToTextInput()
												.isInvalid
										}
									/>
								</div>
								<TextInput
									{...formHandler.for("urlSuffix")
										.attachToTextInput()}
									attachedOn="left"
									placeholder={placeholder}
								/>
							</div>
							<ValidationError
								{...formHandler
									.for("urlSuffix")
									.attachToValidationError()}
							/>
						</FormItem>
					)}

					<AppDestinationWindowSelectFormItem
						attachable={formHandler.for("destinationWindow")}
					/>
				</>
			</EditAppBase>
		);
	}
);

EditAzureApp.displayName = "EditAzureApp";

function convertToSaveModel(
	initialSettings: EditAppSettings<EditAzureAppViewModel>
): SaveAzureAppAppModel {
	const {
		urlSuffix,
		destinationWindow,
		maintenanceWindow,
		conditionalAccessSettings
	} = initialSettings
		.model
		.app;

	return {
		...initialGenericState(initialSettings),
		urlSuffix,
		destinationWindow,
		maintenanceSettings: initialMaintenanceWindowState(maintenanceWindow),
		conditionalAccessSettings: initialConditionalAccessState(conditionalAccessSettings)
	};
}

